import React, { useState } from 'react';

const CartTable = ({ cart, handleQtyChange, handleUomChange, handleRateChange, removeFromCart, isReadOnly, calculateItemFinancials }) => {
    const [expandedRows, setExpandedRows] = useState({});

    const toggleRow = (id) => {
        setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
    };

    if (cart.length === 0) {
        return <p className="text-center text-muted py-4">No items added to the cart yet.</p>;
    }

    return (
        <table className="table table-borderless table-sm align-middle">
            <thead>
                <tr className="border-bottom">
                    <th>Item</th>
                    <th className="text-center">Qty & UOM</th>
                    <th className="text-end">Rate Type</th>
                    <th className="text-end">Total</th>
                    {!isReadOnly && <th></th>}
                </tr>
            </thead>
            <tbody>
                {cart.map((item, index) => {
                    const rowKey = item.product_id || item.id || index;
                    const isExpanded = !!expandedRows[rowKey];
                    const fin = calculateItemFinancials(item);

                    return (
                        <React.Fragment key={rowKey}>
                            <tr>
                                <td>
                                    <div className="d-flex align-items-center">
                                        <button 
                                            className="btn btn-sm btn-link p-0 me-2 text-decoration-none" 
                                            onClick={() => toggleRow(rowKey)}
                                            title="View Adjustments Breakdown"
                                            type="button"
                                        >
                                            <i className={`ti ${isExpanded ? 'ti-circle-minus text-danger' : 'ti-circle-plus text-primary'} f-s-16`}></i>
                                        </button>
                                        <div>
                                            <span className="d-block text-truncate fw-medium" style={{ maxWidth: '140px' }} title={item.name}>
                                                {item.name}
                                            </span>
                                            {fin.adjustments.length > 0 && (
                                                <small className="text-muted d-block" style={{ fontSize: '10px' }}>
                                                    Includes {fin.adjustments.length} adjustment(s)
                                                </small>
                                            )}
                                        </div>
                                    </div>
                                </td>
                                <td className="text-center">
                                    <div className="d-flex align-items-center justify-content-center gap-1">
                                        <input 
                                            type="number" 
                                            className="form-control form-control-sm text-center p-1" 
                                            style={{ width: '50px' }} 
                                            value={item.qty} 
                                            onChange={(e) => handleQtyChange(rowKey, e.target.value)}
                                            disabled={isReadOnly}
                                        />
                                        
                                        {/* UOM SELECTOR */}
                                        {isReadOnly || !item.product_ref ? (
                                            <span className="badge bg-secondary">
                                                {item.product_ref 
                                                    ? (parseInt(item.uom_id) === parseInt(item.product_ref.base_uom_id) 
                                                        ? item.product_ref.base_uom?.short_name 
                                                        : item.product_ref.product_units?.find(u => parseInt(u.uom_id) === parseInt(item.uom_id))?.uom?.short_name)
                                                    : `UOM ${item.uom_id}`}
                                            </span>
                                        ) : (
                                            <select 
                                                className="form-select form-select-sm"
                                                style={{ width: 'auto', minWidth: '60px', padding: '0.25rem 1.5rem 0.25rem 0.5rem' }}
                                                value={item.uom_id || ''}
                                                onChange={(e) => handleUomChange(rowKey, e.target.value)}
                                            >
                                                <option value={item.product_ref.base_uom_id}>{item.product_ref.base_uom?.short_name || 'Base'}</option>
                                                {item.product_ref.product_units?.map(pu => (
                                                    <option key={pu.uom_id} value={pu.uom_id}>{pu.uom?.short_name || `UOM ${pu.uom_id}`}</option>
                                                ))}
                                            </select>
                                        )}
                                    </div>
                                </td>
                              <td className="text-end">
                                    {/* RATE TYPE SELECTOR */}
                                    {isReadOnly ? (
                                        <div className="d-flex flex-column align-items-end">
                                             <span>₹{fin.effectiveUnitRate.toFixed(2)}</span>
                                             {fin.adjustments.length > 0 && (
                                                <small className="text-muted" style={{fontSize:'10px'}}>Base: ₹{fin.basePrice.toFixed(2)}</small>
                                            )}
                                        </div>
                                    ) : !item.product_ref ? (
                                        <span>₹{fin.effectiveUnitRate.toFixed(2)}</span>
                                    ) : (
                                        <div className="d-flex flex-column align-items-end">
                                            <select 
                                                className="form-select form-select-sm mb-1 text-end"
                                                style={{ width: 'auto', minWidth: '100px', padding: '0.25rem 1.5rem 0.25rem 0.5rem' }}
                                                value={item.rate_id || ''}
                                                onChange={(e) => handleRateChange(rowKey, e.target.value)}
                                            >
                                                {/* Filter rates to only show those compatible with the currently selected UOM */}
                                                {item.product_ref.rates?.filter(r => parseInt(r.uom_id) === parseInt(item.uom_id)).length === 0 && (
                                                    <option value="" disabled>No Rates Available</option>
                                                )}
                                                {item.product_ref.rates?.filter(r => parseInt(r.uom_id) === parseInt(item.uom_id)).map(r => {
                                                    const rName = r.rate_type?.name || `Type ${r.rate_type_id}`;
                                                    const rPrice = parseFloat(r.calculated_final_price || r.base_price).toFixed(2);
                                                    return (
                                                        <option key={r.id} value={r.id}>
                                                            {rName} - ₹{rPrice}
                                                        </option>
                                                    )
                                                })}
                                            </select>
                                            {fin.adjustments.length > 0 && (
                                                <small className="text-muted" style={{fontSize:'10px'}}>Eff: ₹{fin.effectiveUnitRate.toFixed(2)}</small>
                                            )}
                                        </div>
                                    )}
                                </td>
                                <td className="text-end fw-bold">₹{fin.lineTotal.toFixed(2)}</td>
                                {!isReadOnly && (
                                    <td className="text-end">
                                        <button className="btn btn-sm btn-light text-danger" type="button" onClick={() => removeFromCart(rowKey)}>
                                            <i className="ti ti-trash"></i>
                                        </button>
                                    </td>
                                )}
                            </tr>

                            {/* Expandable Tax/Discount Breakdown View */}
                            {isExpanded && (
                                <tr className="bg-light border-bottom">
                                    <td colSpan={isReadOnly ? "4" : "5"} className="p-0">
                                        <div className="px-4 py-3" style={{ fontSize: '0.85rem' }}>
                                            <div className="row mb-1">
                                                <div className="col-8 text-muted">Base Amount ({fin.qty} x ₹{fin.basePrice.toFixed(2)})</div>
                                                <div className="col-4 text-end">₹{fin.baseAmount.toFixed(2)}</div>
                                            </div>
                                            
                                            {/* Deductions (Discounts) */}
                                            {fin.adjustments.filter(a => a.is_subtraction).map((adj, i) => {
                                                const adjAmount = adj.percentage 
                                                    ? (fin.baseAmount * parseFloat(adj.percentage)) / 100 
                                                    : parseFloat(adj.amount || 0);
                                                return (
                                                    <div className="row mb-1 text-danger" key={`ded-${i}`}>
                                                        <div className="col-8">- {adj.description || 'Discount'} {adj.percentage ? `(${parseFloat(adj.percentage)}%)` : ''}</div>
                                                        <div className="col-4 text-end">- ₹{adjAmount.toFixed(2)}</div>
                                                    </div>
                                                );
                                            })}

                                            {/* Taxable Amount Line */}
                                            {fin.totalDeductions > 0 && (
                                                <div className="row mb-1 border-top pt-1">
                                                    <div className="col-8 fw-bold text-muted">Taxable Amount</div>
                                                    <div className="col-4 text-end fw-bold">₹{fin.taxableAmount.toFixed(2)}</div>
                                                </div>
                                            )}

                                            {/* Additions (Taxes like CGST/SGST) */}
                                            {fin.adjustments.filter(a => !a.is_subtraction).map((adj, i) => {
                                                const adjAmount = adj.percentage 
                                                    ? (fin.taxableAmount * parseFloat(adj.percentage)) / 100 
                                                    : parseFloat(adj.amount || 0);
                                                return (
                                                    <div className="row mb-1 text-primary" key={`add-${i}`}>
                                                        <div className="col-8">+ {adj.description || 'Tax'} {adj.percentage ? `(${parseFloat(adj.percentage)}%)` : ''}</div>
                                                        <div className="col-4 text-end">+ ₹{adjAmount.toFixed(2)}</div>
                                                    </div>
                                                );
                                            })}

                                            <div className="row mt-2 border-top pt-2 fw-bold text-success">
                                                <div className="col-8">Net Line Total</div>
                                                <div className="col-4 text-end">₹{fin.lineTotal.toFixed(2)}</div>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </React.Fragment>
                    );
                })}
            </tbody>
        </table>
    );
};

export default CartTable;